// Derive streak / XP / level from time_entries. No persistence — recomputed on read.
import { durationMin, todayIso, pad } from './format.js';

export const LEVELS = [
  { threshold: 0,    name: 'Lehrling' },
  { threshold: 500,  name: 'Geselle' },
  { threshold: 1500, name: 'Profi' },
  { threshold: 3000, name: 'Experte' },
  { threshold: 6000, name: 'Meister' },
];

const MIN_DAY_MINUTES = 30; // a "tracked day" needs at least this many minutes logged
const EARLY_CUTOFF    = '09:00';
const XP_PER_DAY      = 10;
const XP_EARLY_BONUS  = 5;

function isoFromDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function isWeekend(d) { return d.getDay() === 0 || d.getDay() === 6; }

// Compute per-user gamification. `entries` should already be filtered to the user.
export function computeGamification(entries, today = new Date()) {
  // Aggregate minutes + earliest start per day
  const byDate = {};
  for (const e of entries) {
    const mins = durationMin(e.from_time, e.to_time);
    const slot = (byDate[e.entry_date] ||= { minutes: 0, earliest: e.from_time });
    slot.minutes += mins;
    if (e.from_time < slot.earliest) slot.earliest = e.from_time;
  }

  const trackedDays = Object.entries(byDate).filter(([, v]) => v.minutes >= MIN_DAY_MINUTES);
  const earlyDays   = trackedDays.filter(([, v]) => v.earliest < EARLY_CUTOFF);

  const xp = trackedDays.length * XP_PER_DAY + earlyDays.length * XP_EARLY_BONUS;

  // Streak: consecutive weekdays ending today (or last weekday if weekend) with >= MIN_DAY_MINUTES.
  // If today is a weekday and NOT yet tracked, we don't break the streak — we count from yesterday.
  let cursor = new Date(today);
  cursor.setHours(0, 0, 0, 0);
  while (isWeekend(cursor)) cursor.setDate(cursor.getDate() - 1);

  // If today (weekday) has no tracked entry yet, move back one weekday to give the user the day
  if (!(byDate[isoFromDate(cursor)]?.minutes >= MIN_DAY_MINUTES)) {
    cursor.setDate(cursor.getDate() - 1);
    while (isWeekend(cursor)) cursor.setDate(cursor.getDate() - 1);
  }

  let streak = 0;
  while (true) {
    const iso = isoFromDate(cursor);
    if ((byDate[iso]?.minutes ?? 0) >= MIN_DAY_MINUTES) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
      while (isWeekend(cursor)) cursor.setDate(cursor.getDate() - 1);
    } else break;
  }

  // Level lookup
  let current = LEVELS[0];
  let next = null;
  for (let i = 0; i < LEVELS.length; i++) {
    if (xp >= LEVELS[i].threshold) {
      current = LEVELS[i];
      next = LEVELS[i + 1] || null;
    }
  }

  const progress = next
    ? Math.min(100, Math.round(((xp - current.threshold) / (next.threshold - current.threshold)) * 100))
    : 100;

  return {
    streak,
    xp,
    level: current.name,
    nextLevel: next?.name || null,
    xpForNext: next ? next.threshold - xp : 0,
    xpInLevel: xp - current.threshold,
    xpLevelSpan: next ? next.threshold - current.threshold : 0,
    progress,
    trackedDays: trackedDays.length,
    earlyDays: earlyDays.length,
  };
}
